import { Controller } from "@hotwired/stimulus"

// facilities--leg-position — one short line under a leg's Position field
// saying what saving it would do — free, another leg's (a swap, when the leg
// being edited has a position to give), past the aisle's last upright — and,
// unless the position typed is free, which ones are: "Taken by Leg 2 · Free:
// 1, 3+".
//
// It reads the aisle as the form was rendered, and the server decides either
// way (`FacilitiesAsset#position_free_in_group`). A swap is asked for, never
// assumed: the hidden `swap` field is "1" only while the typed position is
// another leg's, and the form then carries a Turbo confirm saying who moves
// where (`warning::` is the amber dialog in application.js). Without JS the
// field stays "0" and a taken position is refused as it always was.
//
// Values:
//   taken     {"<position from 1>": "<leg name>"}, without the leg edited
//   uprights  the aisle's upright_count; 0 when it has none
//   current   the edited leg's stored position from 1; 0 for none or a new leg
//   max       the highest position there can be
//   messages  translated templates with %{…} placeholders
const RANGES_SHOWN = 6

export default class extends Controller {
  static targets = ["input", "hint", "status", "free", "swap"]
  static values = {
    taken: Object,
    uprights: Number,
    current: Number,
    max: Number,
    messages: Object,
  }

  connect() {
    // A failed save renders its own error under the field for this value;
    // until the value changes, the line below only lists the free positions.
    this.renderedWithError =
      this.inputTarget.getAttribute("aria-invalid") === "true"
    this.renderedValue = this.inputTarget.value
    this.update()
  }

  disconnect() {
    this.#confirm(null)
  }

  update() {
    const { tone, text, swapWith } = this.#status()
    const status =
      this.renderedWithError && this.inputTarget.value === this.renderedValue
        ? ""
        : text
    const free = tone === "success" ? "" : this.#freeText()

    this.statusTarget.textContent = status
    this.statusTarget.dataset.tone = tone
    this.freeTarget.textContent = status && free ? ` · ${free}` : free
    this.hintTarget.hidden = !status && !free

    this.swapTarget.value = swapWith ? "1" : "0"
    this.#confirm(
      swapWith
        ? this.#fill("swap_confirm", {
            leg: swapWith,
            current: this.currentValue,
          })
        : null,
    )
  }

  // ── what the typed position means ──────────────────────────────────────

  #status() {
    const raw = this.inputTarget.value.trim()
    if (raw === "") return { tone: "info", text: this.#fill("unplaced") }

    const position = /^\d+$/.test(raw) ? Number(raw) : null
    if (position === null || position < 1 || position > this.maxValue) {
      return {
        tone: "danger",
        text: this.#fill("out_of_reach", { max: this.maxValue }),
      }
    }
    if (this.uprightsValue > 0 && position > this.uprightsValue) {
      return {
        tone: "danger",
        text: this.#fill("beyond", { count: this.uprightsValue }),
      }
    }
    if (position === this.currentValue)
      return { tone: "info", text: this.#fill("current") }

    const holder = this.takenValue[String(position)]
    if (holder === undefined)
      return { tone: "success", text: this.#fill("free") }
    if (this.currentValue > 0) {
      return {
        tone: "warning",
        text: this.#fill("swap", { leg: holder }),
        swapWith: holder,
      }
    }
    return { tone: "danger", text: this.#fill("taken", { leg: holder }) }
  }

  // ── the positions still free ───────────────────────────────────────────

  // With a count of uprights: every free one up to it. Without: the gaps below
  // the highest leg, then "N+" — the rack grows to wherever a leg goes.
  #freeText() {
    const taken = new Set(Object.keys(this.takenValue).map(Number))
    const highest = Math.max(0, ...taken)
    const last = this.uprightsValue > 0 ? this.uprightsValue : highest
    const free = []
    for (let position = 1; position <= last; position++) {
      if (!taken.has(position)) free.push(position)
    }

    const parts = this.#ranges(free)
    if (this.uprightsValue === 0 && highest < this.maxValue) {
      parts.push(`${highest + 1}+`)
    }
    if (parts.length === 0) return this.#fill("none_free")

    const shown =
      parts.length > RANGES_SHOWN
        ? [...parts.slice(0, RANGES_SHOWN), "…"]
        : parts
    return this.#fill("free_list", { list: shown.join(", ") })
  }

  #ranges(positions) {
    const ranges = []
    for (const position of positions) {
      const range = ranges[ranges.length - 1]
      if (range && range[1] === position - 1) range[1] = position
      else ranges.push([position, position])
    }
    return ranges.map(([from, to]) =>
      from === to ? `${from}` : `${from}–${to}`,
    )
  }

  // ── output ─────────────────────────────────────────────────────────────

  // Only ever clears what it set: the form carries no confirm of its own.
  #confirm(message) {
    const form = this.element.closest("form")
    if (!form) return

    if (message) {
      form.dataset.turboConfirm = `warning::${message}`
      form.dataset.turboConfirmTitle = this.#fill("swap_title")
    } else if (form.dataset.turboConfirm?.startsWith("warning::")) {
      delete form.dataset.turboConfirm
      delete form.dataset.turboConfirmTitle
    }
  }

  #fill(key, vars = {}) {
    const template = this.messagesValue[key] ?? ""
    return template.replace(/%\{(\w+)\}/g, (_, name) =>
      String(vars[name] ?? ""),
    )
  }
}
